/*@flow*/
import type { Element } from "react"; /** @jsx jsx */
import { useState, useEffect } from "react";
import { css, jsx } from "@emotion/core";
import capitalize from "underscore.string/capitalize";
import downloadSVG from "lib/downloadSVG";
import downloadJSON from "lib/downloadJSON";
import toast from "lib/toast";
import qs from "qs";
import MarkerColorPicker from "comps/MarkerColorPicker";
import basemaps from "lib/basemaps";
import * as db from "lib/db";
import useCommands from "./useCommands";
import useContextState from "./useContextState";
import useQueryParams from "./useQueryParams";
import LTCloseButton from "./LTCloseButton";
import cx from "classnames";
import * as I from "immutable";
import { q } from "ops";
import useDownloadToaster from "lib/useDownloadToaster";
import { clearStorage } from "comps/City/useFilterLocalStorageSync";
import { ltButtonBarBg } from "./colors";

function getFeatureCollection(mapObj) {
  const layers = mapObj._layers;
  const features = Object.values(layers)
    .map((v: any) => v.toGeoJSON && v.toGeoJSON())
    .filter(Boolean);
  if (features.length > 0) {
    return { type: "FeatureCollection", features };
  }
  return null;
}

function LTTBButton({ testid, text, onChange, icon = "fa" }) {
  function onClick() {
    onChange && onChange();
  }

  return (
    <div
      data-testid={testid}
      className="d-flex justify-content-between cursor-pointer mb-4"
      onClick={onClick}
    >
      <div>{text}</div>
      <div>
        <i className={cx(icon)} />
      </div>
    </div>
  );
}

function LTTBToggleButton({
  fieldName,
  testid,
  text,
  onChange,
}: {
  fieldName: string,
  testid: string,
  text: React$Node,
  onChange?: (string) => void,
}) {
  const { [fieldName]: isActive } = useContextState();
  const cmd = useCommands();

  function onClick() {
    cmd.update([fieldName], (v) => !v);
    onChange && onChange(!isActive);
  }

  return (
    <div
      data-testid={testid}
      className="d-flex justify-content-between cursor-pointer mb-4"
      onClick={onClick}
    >
      <div>{text}</div>
      <div>
        <i
          className={cx("fa", {
            "fa-toggle-off": !isActive,
            "fa-toggle-on": isActive,
          })}
        />
      </div>
    </div>
  );
}

function LTTBMoreButton({ testid, text, children }) {
  const [isOpen, setOpen] = useState(false);
  const onClick = () => setOpen((v) => !v);

  return (
    <div data-testid={testid} className="user-select-none">
      <div
        className="d-flex justify-content-between cursor-pointer mb-4"
        onClick={onClick}
      >
        <div>{text}</div>
        <div>
          <i
            className={cx("fa", {
              "fa-caret-down": !isOpen,
              "fa-caret-up": isOpen,
            })}
          />
        </div>
      </div>
      {isOpen ? <div className="pb-2 mb-2">{children}</div> : null}
    </div>
  );
}

function LTTBDownloadButton({
  testid,
  text,
  onClick,
  isLoading,
}: {
  testid: string,
  text: string,
  onClick: Function,
  isLoading?: boolean,
}) {
  return (
    <div
      data-testid={testid}
      className={cx("d-flex justify-content-between cursor-pointer mb-4")}
      onClick={onClick}
    >
      <div>{text}</div>
      <div>
        <i
          className={cx("fa", {
            "fa-download": !isLoading,
            "fa-spinner fa-spin": isLoading,
          })}
        />
      </div>
    </div>
  );
}

function LTTBDownloadSVGButton({
  fileName = "citiesense-dots.svg",
}: {
  fileName?: string,
}) {
  const onClick = () => {
    const selector = ".leaflet-overlay-pane svg";
    const node = document.querySelector(selector);
    const data = node && node.outerHTML;
    if (data) {
      downloadSVG({ fileName, data });
    } else {
      // Show an error if data is blank
      toast("No data to export.");
    }
  };

  return (
    <LTTBDownloadButton
      testid="LTTBDownloadSVGButton"
      text="Export dots and labels as SVG"
      onClick={onClick}
    />
  );
}

function LTTBMarkerColorButton() {
  return (
    <LTTBMoreButton text="Marker colors" testid="LTTBMarkerColorButton">
      <MarkerColorPicker />
    </LTTBMoreButton>
  );
}

const LTTBBasemapButtonDropdown = () => {
  const initValue = db.read("basemap") || "quiet";
  const [basemapName, setBasemapName] = useState(initValue);
  const cmds = useCommands();

  return (
    <div
      css={css(`
        background: ${ltButtonBarBg};
        color: #333;
      `)}
      className="p-4"
    >
      <select
        data-testid="SelectBasemap"
        value={basemapName}
        className="form-control"
        onChange={(e) => {
          const { value } = e.target;
          setBasemapName(value);
          cmds.set(["decks", "basemap", "url"], basemaps[value]);
          db.write("basemap", value);
          window.location.reload();
        }}
      >
        <option value=""> -- Select a basemap -- </option>
        {Object.keys(basemaps).map((v) => (
          <option key={v} value={v}>
            {capitalize(v)}
          </option>
        ))}
      </select>
    </div>
  );
};

function LTTBBasemapButton() {
  return (
    <LTTBMoreButton text="Basemaps" testid="LTTBBasemapButton">
      <LTTBBasemapButtonDropdown />
    </LTTBMoreButton>
  );
}

function LTTBDownloadCSVButton({
  fileName = "records.zip",
}: {
  fileName?: string,
}) {
  // Add area if from global query to the fitler set.
  const areaId = useQueryParams().get("area_id");
  let filter = useQueryParams().get("filter") || I.Map();
  if (areaId) {
    filter = filter.set("area_id", areaId);
  }

  // Bbox alone is not enough.
  const disabled = filter.filterNot((_, k) => k === "bbox").isEmpty();
  const { scheduleDownload, isLoading } = useDownloadToaster();

  const onClick = () => {
    if (isLoading) return;
    if (disabled) {
      toast("Cannot export. Add a few filters.");
    } else {
      // Provide feedback.
      scheduleDownload({ filter: filter.toJS() });
    }
  };

  return (
    <LTTBDownloadButton
      testid="LTTBDownloadCSVButton"
      text="Export records as CSV"
      onClick={onClick}
      isLoading={isLoading}
    />
  );
}

function LTTBToggleLabelsButton() {
  const toggleLabels = (isEnabled) => {
    q((v) => v.setIn(["load", "showLabels"], isEnabled));
  };

  return (
    <LTTBToggleButton
      fieldName="toolsShowLabels"
      testid="LTTBShowLabelsButton"
      text="Show labels"
      onChange={toggleLabels}
    />
  );
}

function LTTBClearCacheButton() {
  const onChange = () => {
    clearStorage();
    window.location = "/map";
  };

  return (
    <LTTBButton
      testid="LTTBClearCacheButton"
      text="Clear query cache"
      icon="far fa-trash"
      onChange={onChange}
    />
  );
}

function LTTBShowImagesInListViewButton() {
  return (
    <LTTBToggleButton
      fieldName="showImagesInListView"
      testid="LTTBShowImagesInListViewButton"
      text="Show images in List View"
    />
  );
}

function LTTBToggleShapesButton() {
  const toggleShapes = (isEnabled) => {
    q((v) => v.setIn(["load", "showShapes"], isEnabled));
  };

  return (
    <LTTBToggleButton
      fieldName="toolsShowShapes"
      testid="LTTBShowLabelsButton"
      text="Show shapes"
      onChange={toggleShapes}
    />
  );
}

function LTTBDownloadGeoJsonButton() {
  const onClick = () => {
    const data = getFeatureCollection(window.cityMap);
    if (data) {
      downloadJSON({ data });
    } else {
      // Show an error if data is blank
      toast("No data to export.");
    }
  };

  return (
    <LTTBDownloadButton
      testid="LTTBDownloadGeoJsonButton"
      text="Export dots as GeoJSON"
      onClick={onClick}
    />
  );
}

// Abbrev: LTTB
function LTToolsBody() {
  return (
    <div data-testid="LTToolsBody" className="pl-4 pr-4 pt-4 pb-2">
      <LTTBToggleLabelsButton />
      <LTTBToggleShapesButton />
      <LTTBShowImagesInListViewButton />
      <LTTBDownloadSVGButton />
      <LTTBDownloadCSVButton />
      <LTTBMarkerColorButton />
      <LTTBBasemapButton />
      <LTTBClearCacheButton />
      <LTTBDownloadGeoJsonButton />
    </div>
  );
}

export default function LTTools(): Element<string> {
  return (
    <div
      data-testid="LTTools"
      className="h-100"
      css={css(`
      color: #333;
    `)}
    >
      <div className="p-4 d-flex border-bottom border-light-white justify-content-between align-items-center">
        <h5 className="text-uppercase m-0 font-weight-bold">TOOLS</h5>
        <LTCloseButton color="#333" />
      </div>
      <LTToolsBody />
    </div>
  );
}
